import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ImagePlus, Trash2 } from 'lucide-react';

import type { User } from '@tasks/contracts';

import { removeUserAvatar, uploadUserAvatar } from '../../api/users';
import { Avatar } from '../../components/Avatar';
import { Button } from '../../components/Button';
import { humanizeError } from '../../lib/errors';
import styles from './AdminPages.module.css';

/** Аватар в админской карточке: Admin меняет фото человеку или агенту (UC-USR-09). */
export function UserAvatarEditor({
  user,
  onChanged,
}: {
  readonly user: User;
  readonly onChanged: (user: User) => void;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: (file: File) => uploadUserAvatar(user.id, file),
    onSuccess: (updated) => {
      setError(null);
      onChanged(updated);
    },
    onError: (cause: unknown) => setError(humanizeError(cause).message),
  });

  const remove = useMutation({
    mutationFn: () => removeUserAvatar(user.id),
    onSuccess: (updated) => {
      setError(null);
      onChanged(updated);
    },
    onError: (cause: unknown) => setError(humanizeError(cause).message),
  });

  return (
    <div className={styles['avatarEditor']}>
      <Avatar user={user} size="md" showTooltip={false} />
      <div className={styles['avatarActions']}>
        <input
          ref={fileRef}
          className={styles['fileInput']}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          aria-label="Аватар пользователя"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (!file) return;
            if (file.size > 5 * 1024 * 1024) {
              setError('Аватар больше 5 МБ');
              return;
            }
            upload.mutate(file);
          }}
        />
        <div className={styles['avatarButtons']}>
          <Button
            size="sm"
            variant="secondary"
            icon={<ImagePlus size={14} />}
            loading={upload.isPending}
            onClick={() => fileRef.current?.click()}
          >
            Загрузить фото
          </Button>
          {user.avatarUrl ? (
            <Button
              size="sm"
              variant="ghost"
              icon={<Trash2 size={14} />}
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Удалить
            </Button>
          ) : null}
        </div>
        <span className="u-muted">PNG, JPEG, GIF или WebP до 5 МБ.</span>
        {error ? (
          <span className={styles['formError']} role="alert">
            {error}
          </span>
        ) : null}
      </div>
    </div>
  );
}
